import React, {useCallback, useContext} from 'react';
import {useSelector} from 'react-redux';

import {MessageContext} from 'src/contexts/message-context';
import {selectAccountLicense} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {CrmType} from 'src/openapi/admin/models/CrmType';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import Loading, {Types as LoadingTypes} from '@pb/kit-component-loading';
import {Tooltip} from '@pb/kit-component-popups';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {BulkSync} from './bulk-sync';
import {ImportSyncedEntities} from './import-synced-entities';

import type {Dispatch, SetStateAction} from 'react';
import type {MessageContextType} from 'src/contexts/message-context';
import type {CrmAccountSettings} from 'src/openapi/admin/models/CrmAccountSettings';
import type {CrmSyncType} from 'src/openapi/admin/models/CrmSyncType';
import type {EntitySettings} from 'src/openapi/admin/models/EntitySettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {ImportEntitySettings} from 'src/openapi/admin/models/ImportEntitySettings';
import type {LinkageStatistics} from 'src/openapi/admin/models/LinkageStatistics';
import type {SalesforceAccountSettings} from 'src/openapi/admin/models/SalesforceAccountSettings';
import type {CheckBoxChangeData} from 'src/typedef';

type SettingsState = SalesforceAccountSettings | CrmAccountSettings;
type EntitySettingsKey = Extract<keyof SettingsState, 'accountSettings' | 'contactSettings' | 'leadSettings'>;
type EntitySettingsValue = ImportEntitySettings | EntitySettings;

type Props = {
    crmType: CrmType,
    disabled?: boolean,
    isSyncLinkageLoading: boolean,
    isLinkageStatisticLoading: boolean,
    linkageStatisticLoadingError: Error | null,
    orgId?: string,

    settings: SalesforceAccountSettings | CrmAccountSettings,
    linkageStatistic: LinkageStatistics | null,

    onSyncLinkageStatistic: () => void,
    onChangeSettings: Dispatch<SetStateAction<SalesforceAccountSettings | CrmAccountSettings>>,
};

export const EntitiesSection = (props: Props) => {
    const license = useSelector(selectAccountLicense);
    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);

    const {
        crmType,
        disabled,
        isSyncLinkageLoading,
        isLinkageStatisticLoading,
        linkageStatisticLoadingError,
        orgId,

        linkageStatistic,
        settings,

        onSyncLinkageStatistic,
        onChangeSettings,
    } = props;

    const changeSettings = useCallback((changeData: CheckBoxChangeData) => {
        const [entity, property] = changeData.name.split('-') as [EntitySettingsKey, keyof EntitySettingsValue];

        onChangeSettings(prevState => {
            const current = prevState[entity];
            if (!current || typeof current !== 'object') {
                return prevState;
            }

            return {
                ...prevState,
                [entity]: {
                    ...(current as EntitySettingsValue),
                    [property]: changeData.event.currentTarget.checked,
                },
            };
        });
    }, [onChangeSettings]);

    const changeAlerts = useCallback((changeData: CheckBoxChangeData) => {
        onChangeSettings(prevState => ({
            ...prevState,
            alertsEnabled: changeData.event.currentTarget.checked,
        }));
    }, [onChangeSettings]);

    const salesforceSettings = crmType === CrmType.SFCRM ? settings as SalesforceAccountSettings : null;

    return (
        <FormSection headline="Entities">
            <Group direction="v">
                <CheckBox
                    disabled={disabled}
                    label="Enable alerts"
                    checked={settings.alertsEnabled}
                    onCheckBoxChange={changeAlerts}
                />
                <Table borderedGrid={true}>
                    <Head>
                        <Cell>Entity type</Cell>
                        <Cell className="text-align_center">
                            <Group justify="center">
                                {isLinkageStatisticLoading ? <Loading type={LoadingTypes.ICON}/> : null}
                                {linkageStatisticLoadingError ? (
                                    <BasicIconButton
                                        iconProps={{type: Icons.ERROR}}
                                        onClick={() => {
                                            setErrorMessage(getError({
                                                error: linkageStatisticLoadingError,
                                            }));
                                        }}
                                    />
                                ) : null}
                                Linked PB IDs
                            </Group>
                        </Cell>
                        <Cell className="text-align_center">
                            <Group justify="center">
                                {isLinkageStatisticLoading ? <Loading type={LoadingTypes.ICON}/> : null}
                                {linkageStatisticLoadingError ? (
                                    <BasicIconButton
                                        iconProps={{type: Icons.ERROR}}
                                        onClick={() => {
                                            setErrorMessage(getError({
                                                error: linkageStatisticLoadingError,
                                            }));
                                        }}
                                    />
                                ) : null}
                                Synced PB IDs
                            </Group>
                        </Cell>
                        <Cell className="text-align_center">Autolink</Cell>
                        <Cell className="text-align_center">Autosync</Cell>
                        <Cell className="text-align_center">Import enabled</Cell>
                    </Head>
                    <Body>
                        <Row>
                            <Cell>Contacts</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.contactsPbIdsLinked.toString()}</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.contactsPbIdsSynced.toString()}</Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.contactSettings.autoLinkEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="contactSettings-autoLinkEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.contactSettings.autoSyncEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="contactSettings-autoSyncEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.contactSettings.importEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="contactSettings-importEnabled"
                                    />
                                </Group>
                            </Cell>
                        </Row>
                        <Row>
                            <Cell>Leads</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.leadsPbIdsLinked.toString()}</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.leadsPbIdsSynced.toString()}</Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.leadSettings.autoLinkEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="leadSettings-autoLinkEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.leadSettings.autoSyncEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="leadSettings-autoSyncEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.leadSettings.importEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="leadSettings-importEnabled"
                                    />
                                </Group>
                            </Cell>
                        </Row>
                        <Row>
                            <Cell>Accounts</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.accountsPbIdsLinked.toString()}</Cell>
                            <Cell className="text-align_center">{linkageStatistic?.accountsPbIdsSynced.toString()}</Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.accountSettings.autoLinkEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="accountSettings-autoLinkEnabled"
                                    />
                                </Group>
                            </Cell>
                            <Cell>
                                <Group justify="center">
                                    <CheckBox
                                        disabled={disabled}
                                        checked={settings.accountSettings.autoSyncEnabled}
                                        onCheckBoxChange={changeSettings}
                                        name="accountSettings-autoSyncEnabled"
                                    />
                                </Group>
                            </Cell>
                            {salesforceSettings ? (
                                <Cell>
                                    <Group justify="center">
                                        <CheckBox
                                            disabled={disabled}
                                            checked={salesforceSettings.accountSettings.importEnabled}
                                            onCheckBoxChange={changeSettings}
                                            name="accountSettings-importEnabled"
                                        />
                                    </Group>
                                </Cell>
                            ) : (<Cell/>)}
                        </Row>
                    </Body>
                </Table>
                <Group>
                    <Tooltip
                        color="light"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <Button
                                ref={anchorRef}
                                {...getAnchorProps()}
                                busy={license?.isActive ? isSyncLinkageLoading : undefined}
                                disabled={disabled || !license?.isActive || !orgId}
                                variant="Type.SECONDARY"
                                onClick={onSyncLinkageStatistic}>
                                Force Reconciliation
                            </Button>
                        )}>
                        Force Entity Linkage Reconciliation
                    </Tooltip>
                    <BulkSync
                        crmType={crmType as unknown as CrmSyncType}
                        disabled={disabled || !license?.isActive || !orgId}
                    />
                    <ImportSyncedEntities
                        crmType={crmType}
                        targetLicenseId={license?.licenseId || 0}
                        disabled={disabled || !orgId}
                    />
                </Group>
            </Group>
        </FormSection>
    );
};
